import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Typography, Stack } from '@mui/material';
import imageCompression from 'browser-image-compression';
import { playlistApi } from '../../api/playlist';
import { albumApi } from '../../api/album';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/api';
import ThumnailEditor from '../../components/common/ThumnailEditor';
import ProgressBar from '../../components/common/ProgressBar';
import { debounce } from 'lodash';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import CancelConfirmDialog from '../../components/dialog/CancelConfirmDialog';
import BasicButton from '../../components/button/BasicButton';
import CustomTextField from '../../components/common/CustomTextField';
import CustomTextareaAutosize from '../../components/common/CustomTextareaAutosize';
import theme from '../../theme';
import ItemAddSection from './ItemAddSection';
import CharacterCounter from '../../components/common/CharacterCounter';
import PageTitle from '../../components/common/PageTitle';
import { useError } from '../../contexts/ErrorContext';

const PLAYLIST_TYPES = {
  TRACK: 'TRACK',
  ALBUM: 'ALBUM',
};

const TITLE_MAX_LENGTH = 60;
const DESCRIPTION_MAX_LENGTH = 5000;
const MIX_THUMBNAIL_UPLOAD_LIMIT_MB = 5;
const MIX_THUMBNAIL_COMPRESSION_OPTIONS = {
  maxSizeMB: 2,
  maxWidthOrHeight: 1600,
  useWebWorker: true,
  initialQuality: 0.82,
};

const schema = yup.object().shape({
  title: yup
    .string()
    .required('제목을 입력하세요.')
    .max(TITLE_MAX_LENGTH, `제목은 ${TITLE_MAX_LENGTH}자를 초과할 수 없습니다.`),
  description: yup
    .string()
    .max(DESCRIPTION_MAX_LENGTH, `내용은 ${DESCRIPTION_MAX_LENGTH}자를 초과할 수 없습니다.`),
});

const PlaylistForm = ({ mode = 'create' }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: '',
      description: '',
    },
  });

  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from'); // 진입 페이지 정보
  const returnPage = searchParams.get('returnPage'); // 마이페이지에서의 이전 페이지
  const returnSort = searchParams.get('returnSort'); // 정렬 옵션
  const [thumbnailImage, setThumbnailImage] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [playlistType, setPlaylistType] = useState(PLAYLIST_TYPES.TRACK);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemSearchText, setItemSearchText] = useState('');
  const [itemOptions, setItemOptions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(mode === 'edit');
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [itemError, setItemError] = useState(false);
  const { showError } = useError();

  // Edit mode: Load existing playlist data
  useEffect(() => {
    if (mode === 'edit' && id) {
      const fetchPlaylist = async () => {
        try {
          const { data } = await playlistApi.getPlaylistById(id);
          reset({ title: data.title, description: data.description });
          setThumbnailPreview(data.imageUrl);
          setPlaylistType(data.type || PLAYLIST_TYPES.TRACK);
          // type에 따라 albums 또는 tracks 로드
          if (data.type === PLAYLIST_TYPES.TRACK) {
            setSelectedItems(data.tracks || []);
          } else {
            setSelectedItems(data.albums || []);
          }
        } catch (err) {
          console.error(err.message || '믹스를 불러오는데 실패했습니다.');
        } finally {
          setLoading(false);
        }
      };

      fetchPlaylist();
    }
  }, [mode, id, reset]);

  const handleThumbnailUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showError('이미지 파일만 업로드할 수 있습니다.');
        return;
      }
      setThumbnailImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setThumbnailPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleThumbnailDelete = () => {
    setThumbnailImage(null);
    setThumbnailPreview('');
  };

  const searchItems = useCallback(
    async (searchText) => {
      if (!searchText.trim()) {
        setItemOptions([]);
        return;
      }
      setIsSearching(true);
      try {
        // 타입에 따라 다른 검색 API 호출
        const response =
          playlistType === PLAYLIST_TYPES.TRACK
            ? await albumApi.searchTracks(searchText)
            : await albumApi.searchAlbums(searchText);

        // 이미 선택된 항목들을 검색 결과에서 제외
        const selectedSourceIds = selectedItems.map((item) => item.sourceId);
        const filteredResults = response.data.filter(
          (item) => !selectedSourceIds.includes(item.sourceId),
        );

        setItemOptions(filteredResults);
      } catch (err) {
        console.error('Error fetching items', err);
      } finally {
        setIsSearching(false);
      }
    },
    [selectedItems, playlistType],
  );

  const debouncedSearch = useMemo(() => debounce((text) => searchItems(text), 500), [searchItems]);

  useEffect(() => {
    debouncedSearch(itemSearchText);
  }, [itemSearchText, debouncedSearch]);

  const handleAddItem = (event, value) => {
    if (value && !selectedItems.find((item) => item.sourceId === value.sourceId)) {
      setSelectedItems([...selectedItems, value]);
      setItemError(false);
    }
    setItemSearchText('');
    setItemOptions([]);
  };

  const handleRemoveItem = (itemToRemove) => {
    setSelectedItems(selectedItems.filter((item) => item.sourceId !== itemToRemove.sourceId));
  };

  const handleTypeChange = (newType) => {
    if (newType === playlistType) return;
    setPlaylistType(newType);
    setSelectedItems([]);
    setItemOptions([]);
    setItemSearchText('');
  };

  const onSubmit = async (formData) => {
    if (selectedItems.length === 0) {
      setItemError(true);
      return;
    }

    try {
      let thumbnailUrl = thumbnailPreview;
      if (thumbnailImage) {
        const compressedThumbnail = await imageCompression(
          thumbnailImage,
          MIX_THUMBNAIL_COMPRESSION_OPTIONS,
        );

        if (compressedThumbnail.size > MIX_THUMBNAIL_UPLOAD_LIMIT_MB * 1024 * 1024) {
          throw new Error(`이미지는 ${MIX_THUMBNAIL_UPLOAD_LIMIT_MB}MB 이하로 업로드해주세요.`);
        }

        const uploadFormData = new FormData();
        uploadFormData.append('image', compressedThumbnail);
        const { data: response } = await api.postAuth('/images/upload', uploadFormData);
        thumbnailUrl = response.data.url;
      }

      const playlistData = {
        title: formData.title,
        description: formData.description,
        imageUrl: thumbnailUrl,
        ...(mode === 'create' && { type: playlistType }),
      };

      // type에 따라 albums 또는 tracks 추가
      if (playlistType === PLAYLIST_TYPES.TRACK) {
        playlistData.tracks = selectedItems.map((item, index) => ({
          sourceId: item.sourceId,
          albumSource: item.albumSource,
          position: index,
        }));
      } else {
        playlistData.albums = selectedItems.map((item, index) => ({
          sourceId: item.sourceId,
          albumSource: item.albumSource,
          position: index,
        }));
      }

      if (mode === 'create') {
        const response = await playlistApi.createPlaylist(playlistData);
        navigate(`/lists/${response.data.slug || response.data.id}`);
      } else {
        await playlistApi.updatePlaylist(id, playlistData);
        navigate(`/lists/${id}`);
      }
    } catch (err) {
      console.error(err.message);
      if (!err.response) {
        showError(err.message || '믹스를 저장하는 중 오류가 발생했습니다.');
      }
    } finally {
      reset();
    }
  };

  const handleFormSubmit = () => {
    if (selectedItems.length === 0) {
      setItemError(true);
    }
    handleSubmit(onSubmit)();
  };

  const handleCancel = () => {
    // 진입 페이지에 따라 다른 경로로 이동
    switch (from) {
      case 'mypage':
        // 마이페이지에서 진입한 경우
        if (mode === 'create') {
          // 생성 모드인 경우 이전 페이지 정보가 있으면 해당 페이지로, 없으면 기본값
          const targetPage = returnPage ? `/mypage/lists?page=${returnPage}` : '/mypage/lists';
          navigate(targetPage);
        } else {
          // 편집 모드인 경우 이전 페이지 정보가 있으면 해당 페이지로, 없으면 기본값
          const targetPage = returnPage ? `/mypage/lists?page=${returnPage}` : '/mypage/lists';
          navigate(targetPage);
        }
        break;
      case 'list':
        // 리스트 목록 페이지에서 진입한 경우 (편집 모드)
        const sortParam = returnSort ? `&sort=${returnSort}` : '';
        const listPage = returnPage
          ? `/lists?page=${returnPage}${sortParam}`
          : `/lists${sortParam}`;
        navigate(listPage);
        break;
      case 'detail':
        // 리스트 상세 페이지에서 편집 버튼으로 진입한 경우
        if (mode === 'edit' && id) {
          navigate(`/lists/${id}`);
        } else {
          navigate('/lists');
        }
        break;
      default:
        // 기본값: 리스트 목록 페이지에서 진입한 경우
        if (mode === 'create') {
          navigate('/lists');
        } else {
          navigate(`/lists/${id}`);
        }
        break;
    }
  };

  return (
    <PageLayout>
      <ProgressBar isLoading={loading || isSubmitting}>
        <Box sx={{ maxWidth: '780px', mx: 'auto' }}>
          <PageTitle sx={{ mb: { xs: '30px', lg: '40px' } }}>플리 만들기</PageTitle>
          <Stack spacing={{ xs: '40px', md: '40px', lg: '50px' }} width={'100%'}>
            <Stack spacing={2.5}>
              {/* 유형 선택 */}
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  유형<span style={{ color: theme.palette.secondary.main }}>*</span>
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <BasicButton
                    variant={playlistType === PLAYLIST_TYPES.TRACK ? 'contained' : 'outlined'}
                    onClick={() => handleTypeChange(PLAYLIST_TYPES.TRACK)}
                    disabled={mode === 'edit' && playlistType !== PLAYLIST_TYPES.TRACK}
                    sx={{
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    곡 플리
                  </BasicButton>
                  <BasicButton
                    variant={playlistType === PLAYLIST_TYPES.ALBUM ? 'contained' : 'outlined'}
                    onClick={() => handleTypeChange(PLAYLIST_TYPES.ALBUM)}
                    disabled={mode === 'edit' && playlistType !== PLAYLIST_TYPES.ALBUM}
                    sx={{
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    앨범 플리
                  </BasicButton>
                </Box>
              </Stack>

              {/* 제목 */}
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  제목<span style={{ color: theme.palette.secondary.main }}>*</span>
                </Typography>
                <CustomTextField
                  fullWidth
                  {...register('title')}
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  placeholder="제목을 입력하세요."
                />
                {!errors.title && (
                  <Box
                    sx={{
                      width: { xs: '100%', md: 'auto' },
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <CharacterCounter
                      currentLength={watch('title')?.length || 0}
                      maxLength={TITLE_MAX_LENGTH}
                    />
                  </Box>
                )}
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  대표 이미지
                </Typography>
                <ThumnailEditor
                  thumbnailPreview={thumbnailPreview}
                  handleThumbnailUpload={handleThumbnailUpload}
                  handleThumbnailDelete={handleThumbnailDelete}
                  type="square"
                />
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  설명
                </Typography>
                <CustomTextareaAutosize
                  minRows={5.5}
                  {...register('description')}
                  error={!!errors.description}
                  placeholder="설명을 입력하세요."
                  maxLength={DESCRIPTION_MAX_LENGTH}
                />
                <Box
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <CharacterCounter
                    currentLength={watch('description')?.length || 0}
                    maxLength={DESCRIPTION_MAX_LENGTH}
                  />
                </Box>
              </Stack>

              {/* 곡/앨범 추가 */}
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  {playlistType === PLAYLIST_TYPES.TRACK ? '곡 추가' : '앨범 추가'}
                  <span style={{ color: theme.palette.secondary.main }}>*</span>
                </Typography>
                <ItemAddSection
                  albumOptions={itemOptions}
                  isSearching={isSearching}
                  albumSearchText={itemSearchText}
                  setAlbumSearchText={setItemSearchText}
                  handleAddAlbum={handleAddItem}
                  selectedAlbums={selectedItems}
                  setSelectedAlbums={setSelectedItems}
                  handleRemoveAlbum={handleRemoveItem}
                  error={itemError}
                  helperText={
                    itemError
                      ? playlistType === PLAYLIST_TYPES.TRACK
                        ? '곡을 하나 이상 추가하세요.'
                        : '앨범을 하나 이상 추가하세요.'
                      : ''
                  }
                  placeholder={
                    playlistType === PLAYLIST_TYPES.TRACK
                      ? '곡 제목 또는 아티스트를 검색하세요.'
                      : '앨범 또는 아티스트를 검색하세요.'
                  }
                  type={playlistType}
                />
              </Stack>
            </Stack>

            {/* 버튼 */}
            <Box width={'100%'} display={'flex'} justifyContent={'center'}>
              <Box display="flex" gap={1} width={{ xs: '100%', md: 'auto' }}>
                <BasicButton
                  fullWidth
                  variant="outlined"
                  onClick={() => setIsCancelDialogOpen(true)}
                  sx={{ width: { md: '200px', lg: '110px' }, height: '48px' }}
                >
                  취소
                </BasicButton>
                <BasicButton
                  type="button"
                  fullWidth
                  disabled={isSubmitting}
                  onClick={handleFormSubmit}
                  sx={{ width: { md: '200px', lg: '110px' }, height: '48px' }}
                >
                  등록
                </BasicButton>
              </Box>
            </Box>
          </Stack>
        </Box>
      </ProgressBar>
      <CancelConfirmDialog
        open={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        onConfirm={handleCancel}
      />
    </PageLayout>
  );
};

export default PlaylistForm;
